// OutcomeEditorTable.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import "./AiTrades.css"; // ✅ Add this CSS file

export default function OutcomeEditorTable() {
  const [docs, setDocs] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [filter, setFilter] = useState("all");
  const [fillStatus, setFillStatus] = useState("all");
  const [dataset, setDataset] = useState("cryptoAnalysis");
  const [preferredDirectionByDoc, setPreferredDirectionByDoc] = useState({});
  const [analyzingAll, setAnalyzingAll] = useState(false);
  const [analyzedDocs, setAnalyzedDocs] = useState({});
  const [loadingDocId, setLoadingDocId] = useState(null);
  const PAGE_SIZE = 100;

  const [pageOffset, setPageOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const isLocal = window.location.hostname === "localhost";
  const baseUrl = isLocal
    ? "http://localhost:5000"
    : "https://www.trendscout.club";

  const toNumSafe = (v) => {
    if (v == null) return null;
    const n = typeof v === "string" ? parseFloat(v.replace(/,/g, "")) : v;
    return Number.isFinite(n) ? n : null;
  };

  const fmt4 = (v) => {
    const n = toNumSafe(v);
    return n == null ? "" : n.toFixed(4);
  };


useEffect(() => {
  const fetchAllDocs = async () => {
    try {
      if (dataset === "aiCompleted") {
        const res = await axios.get(
          `${baseUrl}/api/crypto-analysis?dataset=${dataset}&limit=${PAGE_SIZE}&offset=0`
        );

        const payload = res.data;
        const first = Array.isArray(payload?.docs) ? payload.docs : [];

        setDocs(first);
        setPageOffset(first.length);
        setHasMore(!!payload?.hasMore);
        return;
      }

      // existing behavior for other datasets
      const res = await axios.get(
        `${baseUrl}/api/crypto-analysis?dataset=${dataset}`
      );
      const sorted = res.data.sort(
        (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
      );
      setDocs(sorted);
    } catch (err) {
      console.error("Failed to fetch documents:", err);
    }
  };

  fetchAllDocs();
}, [dataset, baseUrl]);


  useEffect(() => {
  setPageOffset(0);
  setHasMore(false);
}, [dataset]);

const handleLoadNext = async () => {
  if (dataset !== "aiCompleted" || loadingMore || !hasMore) return;

  setLoadingMore(true);
  try {
    const res = await axios.get(
      `${baseUrl}/api/crypto-analysis?dataset=${dataset}&limit=${PAGE_SIZE}&offset=${pageOffset}`
    );

    const payload = res.data;
    const nextDocs = Array.isArray(payload?.docs) ? payload.docs : [];

    setDocs((prev) => [...prev, ...nextDocs]);
    setPageOffset((prev) => prev + nextDocs.length);
    setHasMore(!!payload?.hasMore);
  } catch (err) {
    console.error("Failed to load next page:", err);
    alert("Failed to load more completed docs.");
  } finally {
    setLoadingMore(false);
  }
};


  const handleChange = (id, field, value) => {
    setDocs((prev) =>
      prev.map((doc) => {
        return doc._id === id
          ? {
              ...doc,
              outcome: {
                ...doc.outcome,
                [field]: value, // raw string
              },
            }
          : doc;
      })
    );
  };

  const handleAutoAnalyze = async (id, silent = false) => {
    setLoadingDocId(id);
    try {
      const res = await axios.get(
        `${baseUrl}/api/token-analysis/${id}?dataset=${dataset}`
      );
      const { longOutcome, shortOutcome } = res.data;
      const doc = docs.find((d) => d._id === id);
      if (!doc) return;

      const side = pickSide(doc, preferredDirectionByDoc);
      const isLong = side === "long";

      const outcomeData = isLong ? longOutcome : shortOutcome;

      if (!outcomeData || outcomeData.entryType !== "recorded" ||
          res.data.side !== (isLong ? "LONG" : "SHORT")) {
        throw new Error("This side has no recorded trigger. Existing results were left unchanged.");
      }
      const high = toNumSafe(outcomeData.highest);
      const low = toNumSafe(outcomeData.lowest);
      if (high == null || low == null) {
        throw new Error("No verified post-trigger prices. Existing results were left unchanged.");
      }
      const targetHit = outcomeData.result === "TP Hit" ? "tp"
        : outcomeData.result === "SL Hit" ? "sl" : "none";
      const profitLoss = toNumSafe(outcomeData.gain);
      const triggeredAt = toNumSafe(outcomeData.triggeredAt);
      setDocs((prevDocs) => prevDocs.map((d) => d._id !== id ? d : {
        ...d,
        outcome: {
          ...d.outcome,
          priceReached: fmt4(isLong ? high : low),
          priceReachedOther: fmt4(isLong ? low : high),
          targetHit,
          time: outcomeData.time ?? null,
          profitLoss: profitLoss == null ? "" : profitLoss.toFixed(2),
          daysToOutcome: outcomeData.time != null && triggeredAt != null
            ? ((outcomeData.time - triggeredAt) / 3600000).toFixed(2) : "",
          feedbackSource: "auto",
        },
      }));
      setAnalyzedDocs((prev) => ({ ...prev, [id]: outcomeData.result }));

      if (!silent) {
        alert(`Analysis updated for ${id}`);
      }
    } catch (err) {
      console.error("Auto analysis error:", err);
      if (!silent) alert(err.response?.data?.error || err.message || "Error analyzing this token.");
      setAnalyzedDocs((prev) => ({ ...prev, [id]: "error" }));
    } finally {
      setLoadingDocId(null);
    }
  };

  const handleAnalyzeAll = async () => {
    setAnalyzingAll(true);
    for (let i = 0; i < filteredDocs.length; i++) {
      const doc = filteredDocs[i];
      await handleAutoAnalyze(doc._id, true);
      await new Promise((res) => setTimeout(res, 5000));
    }
    setAnalyzingAll(false);
  };

const handleScoreChange = (id, value) => {
  setDocs(prev =>
    prev.map(doc =>
      doc._id === id
        ? {
            ...doc,
            analysis: {
              ...doc.analysis,
              // keep raw string for the input; we'll parse on save
              score: value,
            },
          }
        : doc
    )
  );
};

const handleAutoErrorMargin = (doc) => {
  const { priceReached, targetHit, time } = doc.outcome || {};
  const up = doc.analysis?.upsideChance;
  const down = doc.analysis?.downsideChance;

  let projectedTP = null;
  let entry = null;

  const side = pickSide(doc, preferredDirectionByDoc);
  const isLong = side === "long";

  if (isLong) {
    projectedTP = doc.longPosition?.tp;
    entry = doc.longPosition?.entry;
  } else {
    projectedTP = doc.shortPosition?.tp;
    entry = doc.shortPosition?.entry;
  }

  if (!projectedTP || !entry) {
    alert("Missing entry/TP on the selected side.");
    return;
  }


  if (!priceReached || !projectedTP || !targetHit || !entry) {
    alert("Missing priceReached, TP, SL, or entry.");
    console.log("⛔ Incomplete data — exiting early.");
    return;
  }

  // 🔹 Error Margin
  const error = (Math.abs(priceReached - projectedTP) / projectedTP) * 100;

  // 🔹 Profit/Loss
  let profitLoss = 0;
  if (targetHit === "tp") {
    profitLoss = isLong
      ? ((projectedTP - entry) / entry) * 100
      : ((entry - projectedTP) / entry) * 100;
  } else if (targetHit === "sl") {
    const stopLoss = isLong ? doc.longPosition?.sl : doc.shortPosition?.sl;
    if (!stopLoss) {
      alert("Missing SL value.");
      console.log("⛔ Missing stop loss — exiting.");
      return;
    }
    profitLoss = isLong
      ? ((stopLoss - entry) / entry) * 100
      : ((entry - stopLoss) / entry) * 100;
  }

  // 🔹 Days to Outcome = Hours
const baseTimestamp = new Date(doc._createdAt || doc.timestamp || "").getTime();
const outcomeTimestamp = doc.outcome?.time || null;


  let hoursToOutcome = "";
  if (outcomeTimestamp) {
    hoursToOutcome = ((outcomeTimestamp - baseTimestamp) / 3600000).toFixed(2);
  } else {
    console.log("⚠️ No outcome time available — skipping time calculation.");
  }

  // Round
  const errorRounded = parseFloat(error.toFixed(2));
  const profitLossRounded = parseFloat(profitLoss.toFixed(2));

  handleChange(doc._id, "errorMargin", errorRounded);
  handleChange(doc._id, "profitLoss", profitLossRounded);
  handleChange(doc._id, "daysToOutcome", hoursToOutcome); // Should show in input
};

const handleDelete = async (id) => {
  const doc = docs.find((d) => d._id === id);
  if (!doc) return;

  const confirmed = window.confirm(`Delete ${doc.token}? This cannot be undone.`);
  if (!confirmed) return;

  try {
    await axios.delete(`${baseUrl}/api/crypto-analysis/${id}`);
    alert("Document deleted.");
    setDocs((prev) => prev.filter((d) => d._id !== id));
  } catch (err) {
    console.error("Delete failed:", err);
    alert("Delete failed.");
  }
};



  const handleSave = async (id) => {
    const doc = docs.find((d) => d._id === id);
    if (!doc) return;

    // Safely parse number fields
    const outcome = { ...doc.outcome };
    ["priceReached", "priceReachedOther", "errorMargin", "profitLoss", "daysToOutcome"].forEach(
      (field) => {
        if (outcome[field] !== undefined && outcome[field] !== "") {
          outcome[field] = parseFloat(outcome[field]);
        }
      }
    );

      // ✅ Prepare analysis.score (optional)
  let analysis = undefined;
  if (doc.analysis && doc.analysis.score !== undefined && doc.analysis.score !== "") {
    const parsed = parseFloat(doc.analysis.score);
    if (!Number.isNaN(parsed)) {
      analysis = { score: parsed };
    }
  }

  setSavingId(id);
  try {
    await axios.post(`${baseUrl}/api/crypto-analysis/${id}`, {
      outcome,
      analysis,      // <-- include if present
      dataset,
    });
    alert("Saved successfully.");
  } catch (err) {
    console.error("Save failed:", err);
    alert("Save failed.");
  } finally {
    setSavingId(null);
  }
};

  const handleFinalize = async (id) => {
    const doc = docs.find((d) => d._id === id);
    if (!doc) return;

    if (
      !window.confirm(
        "Are you sure you want to finalize and move this document to aiCompleted?"
      )
    )
      return;

    try {
      await axios.post(`${baseUrl}/api/crypto-analysis/finalize/${id}`, {
        dataset,
      });
      alert("Document finalized and moved to aiCompleted.");
      // Remove it from current UI
      setDocs((prev) => prev.filter((d) => d._id !== id));
    } catch (err) {
      console.error("Finalize failed:", err);
      alert("Finalize failed.");
    }
  };

  const filteredDocs = docs.filter((d) => {
    const matchesLabel = filter === "all" || d.outcome?.humanLabel === filter;

    const isFilled = !!d.outcome?.priceReached;
    const matchesFill =
      fillStatus === "all" ||
      (fillStatus === "empty" && !isFilled) ||
      (fillStatus === "filled" && isFilled);

    return matchesLabel && matchesFill;
  });

  // --- helpers (component scope) ---
const toNum = (v) => {
  const n = typeof v === "string" ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : NaN;
};

const isFivePctDiff = (entryRaw, tpRaw, tolerance = 0.05) => {
  const entry = toNum(entryRaw);
  const tp = toNum(tpRaw)
  if (!Number.isFinite(entry) || !Number.isFinite(tp) || entry === 0) return false;
  const diff = Math.abs((tp - entry) / entry) * 100;
  return Math.abs(diff - 5) <= tolerance; // 5% ± 0.05%
};

// Decide if THIS doc's row should tint
const rowIsFivePct = (doc) => {
  const prefer = preferredDirectionByDoc[doc._id]; // 'long' | 'short' | undefined

  const longE  = doc.longPosition?.entry;
  const longTP = doc.longPosition?.tp;
  const shortE  = doc.shortPosition?.entry;
  const shortTP = doc.shortPosition?.tp;

  if (prefer === "long")  return isFivePctDiff(longE,  longTP);
  if (prefer === "short") return isFivePctDiff(shortE, shortTP);

  // No preferred side picked: check either side
  return isFivePctDiff(longE, longTP) || isFivePctDiff(shortE, shortTP);
};

// ---- side-selection helpers ----
function getSideFromRecommendation(rec) {
  const s = (rec || "").toLowerCase();
  if (s.includes("buy") || s.includes("long")) return "long";
  if (s.includes("sell") || s.includes("short")) return "short";
  return null;
}

// Priority: (1) user preferred, (2) recommendation, (3) chances
function pickSide(doc, preferredDirectionByDoc) {
  const preferred = preferredDirectionByDoc[doc._id];
  if (preferred === "long" || preferred === "short") return preferred;

  const recSide = getSideFromRecommendation(doc.analysis?.recommendation);
  if (recSide) return recSide;

  const up = doc.analysis?.upsideChance ?? 0;
  const down = doc.analysis?.downsideChance ?? 0;
  return up >= down ? "long" : "short";
}



  return (
    <div className="outcome-table-wrapper">
      <div className="top-margin-ai-edit-tokens">
        <div className="button-group" style={{ marginBottom: "1rem" }}>
          {["cryptoAnalysis", "aitriggered", "aiinactive", "aiCompleted",].map((db) => (
            <button
              key={db}
              className={dataset === db ? "active-db" : ""}
              onClick={() => setDataset(db)}
            >
              {db === "cryptoAnalysis" && "🟢 Active"}
              {db === "aitriggered" && "🚀 Triggered"}
              {db === "aiinactive" && "🛑 Inactive"}
              {db === "aiCompleted" && "✅ Completed"}
            </button>
          ))}
          <button
            className="auto-btn"
            onClick={handleAnalyzeAll}
            disabled={analyzingAll}
          >
            {analyzingAll ? "Analyzing..." : "🔄 Analyze All"}
          </button>
        </div>
        {dataset === "aiCompleted" && (
  <button
    className="auto-btn"
    onClick={handleLoadNext}
    disabled={loadingMore || !hasMore}
    style={{ marginLeft: 10 }}
  >
    {loadingMore ? "Loading..." : hasMore ? "Load next 100" : "No more"}
  </button>
)}

      </div>

      <div className="table-scroll-container">
        <table className="outcome-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Token</th>
              <th>Score</th>
              <th>Price Calc</th>
              <th>Status</th>
              <th>Price Reached</th>
              <th>Price Other</th>
              <th>Target Hit</th>
              <th>Profit/Loss (%)</th>
              <th>Error Margin (%)</th>
              <th>Err Calc</th>
              <th>Hours to Outcome</th>
              <th>AI Accuracy</th>
              <th>Human Label</th>
            {/* <th>Feedback</th>
              <th>Human Note</th>
              <th>AI Reflection</th>*/} 
              <th>Save</th>
              <th>Final</th>
              <th>Delete</th>
            </tr>
          </thead>
          <tbody>
            {filteredDocs.map((doc, index) => (
              <React.Fragment key={doc._id}>
                  <tr
                    onClick={() =>
                      setSelectedDoc(doc._id === selectedDoc?._id ? null : doc)
                    }
                    className={rowIsFivePct(doc) ? "tint-5pct" : ""}
                    style={{
                      cursor: "pointer",
                      // keep selected override if you like:
                      ...(selectedDoc?._id === doc._id ? { backgroundColor: "#236c4bff" } : null),
                    }}
                  >

                  <td className="editTradesTableTd">
                    {doc.timestamp
                      ? new Date(doc.timestamp).toLocaleString("en-US", {
                          month: "short",  // Jun
                          day: "numeric",  // 9
                          hour: "numeric", // 3 PM
                          hour12: true     // use 24-hour if false
                        })
                      : "N/A"}
                  </td>

                  <td className="editTradesTableTd">{doc.token}</td>
                  <td>
                    <input
                      type="number"
                      step="0.01"
                      value={doc.analysis?.score ?? ""}
                      onChange={(e) => handleScoreChange(doc._id, e.target.value)}
                      style={{ width: 90 }}
                    />
                  </td>
                  <td>
                    <button
                      className="auto-btn"
                      onClick={() => handleAutoAnalyze(doc._id)}
                      disabled={loadingDocId === doc._id}
                    >
                      {loadingDocId === doc._id ? "Loading..." : "Analyze"}
                    </button>
                  </td>
                  <td className="editTradesTableTd">
                    {analyzedDocs[doc._id]?.includes("TP")
                      ? "✅ " + analyzedDocs[doc._id]
                      : analyzedDocs[doc._id]?.includes("SL")
                      ? "❌ " + analyzedDocs[doc._id]
                      : analyzedDocs[doc._id] === "Entry Not Reached"
                      ? "⏳"
                      : analyzedDocs[doc._id] === "error"
                      ? "⚠️ Error"
                      : "Entry"}
                  </td>

                  <td className="editTradesTableTd">
                    {doc.outcome?.priceReached || ""}
                  </td>
                    <td className="editTradesTableTd">
                    {doc.outcome?.priceReachedOther || ""}
                  </td>
                  <td>
                    <select
                      value={doc.outcome?.targetHit || ""}
                      onChange={(e) =>
                        handleChange(doc._id, "targetHit", e.target.value)
                      }
                    >
                      <option value="">--</option>
                      <option value="tp">TP</option>
                      <option value="sl">SL</option>
                      <option value="none">Neither</option>
                    </select>
                  </td>
                  <td>
                    <input
                      type="number"
                      value={doc.outcome?.profitLoss || ""}
                      onChange={(e) =>
                        handleChange(doc._id, "profitLoss", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={doc.outcome?.errorMargin || ""}
                      onChange={(e) =>
                        handleChange(doc._id, "errorMargin", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <button
                      onClick={() => handleAutoErrorMargin(doc)}
                      className="auto-btn"
                      title="Auto-calculate Error Margin"
                    >
                      ⚙️
                    </button>
                  </td>
                  <td>
                    <input
                      type="number"
                      value={doc.outcome?.daysToOutcome || ""}
                      onChange={(e) =>
                        handleChange(doc._id, "daysToOutcome", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <select
                      value={doc.outcome?.aiAccuracyRating || ""}
                      onChange={(e) =>
                        handleChange(
                          doc._id,
                          "aiAccuracyRating",
                          e.target.value
                        )
                      }
                    >
                      <option value="">--</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                      <option value="invalid">Invalid</option>
                    </select>
                  </td>
                  <td>
                    <select
                      value={doc.outcome?.humanLabel || ""}
                      onChange={(e) =>
                        handleChange(doc._id, "humanLabel", e.target.value)
                      }
                    >
                      <option value="">--</option>
                      <option value="correct_buy">Correct BUY</option>
                      <option value="correct_sell">Correct SELL</option>
                      <option value="correct_skip">Correct SKIP</option>
                      <option value="should_buy">Should BUY</option>
                      <option value="should_sell">Should SELL</option>
                      <option value="should_skip">Should SKIP</option>
                    </select>
                  </td>
                {/*   <td>
                    <select
                      value={doc.outcome?.feedbackSource || ""}
                      onChange={(e) =>
                        handleChange(doc._id, "feedbackSource", e.target.value)
                      }
                    >
                      <option value="">--</option>
                      <option value="manual">Manual</option>
                      <option value="backtest">Backtest</option>
                      <option value="auto">Automated</option>
                    </select>
                  </td>
                  <td>
                    <textarea
                      value={doc.outcome?.notes || ""}
                      onChange={(e) =>
                        handleChange(doc._id, "notes", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <textarea
                      value={doc.outcome?.aiReflection || ""}
                      onChange={(e) =>
                        handleChange(doc._id, "aiReflection", e.target.value)
                      }
                    />
                  </td>*/}
                  <td>
                    <button
                      className="save-btn"
                      onClick={() => handleSave(doc._id)}
                      disabled={savingId === doc._id}
                    >
                      {savingId === doc._id ? "Saving..." : "Save"}
                    </button>
                  </td>
                  <td>
                    <button
                      className="final-btn"
                      onClick={() => handleFinalize(doc._id)}
                    >
                      Final
                    </button>
                  </td>
                  <td>
                <button
                  className="delete-btn"
                  onClick={() => handleDelete(doc._id)}
                  style={{ color: "red" }}
                >
                  Delete
                </button>
              </td>

                </tr>

                {selectedDoc?._id === doc._id && (
                  <tr className="expanded-row">
                    <td colSpan={19}>
                      <div className="expanded-content">
                        <div className="expanded-section">
                          <h4>📊 General Info</h4>
                          <p>
                            <strong>Price at Analysis:</strong>{" "}
                            {doc.priceAtAnalysis ?? "N/A"}
                          </p>
                          <p>
                            <strong>Timestamp:</strong>{" "}
                            {doc._createdAt
                              ? new Date(doc._createdAt).toLocaleString()
                              : "N/A"}
                          </p>
                          <p>
                            <strong>Recommendation:</strong>{" "}
                            {doc.analysis?.recommendation ?? "N/A"}
                          </p>
                          <p>
                            <strong>Trend Strength:</strong>{" "}
                            {doc.analysis?.trendStrength ?? "N/A"}
                          </p>
                          <p>
                            <strong>Score:</strong>{" "}
                            {doc.analysis?.score ?? "N/A"}
                          </p>
                          <p>
                            <strong>Direction:</strong>{" "}
                            {doc.marketContext?.expectedDirection24h ?? "N/A"}
                          </p>
                          <p>
                            <strong>Trend Phase:</strong>{" "}
                            {doc.marketContext?.trendPhase ?? "N/A"}
                          </p>
                        </div>

                        <div
                          className="expanded-section"
                          style={{
                            border:
                              preferredDirectionByDoc[doc._id] === "long"
                                ? "2px solid #00ff88"
                                : "1px solid #444",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            setPreferredDirectionByDoc((prev) => ({
                              ...prev,
                              [doc._id]: "long",
                            }))
                          }
                        >
                          <h4>📈 Long Position</h4>
                          <p>Entry: {doc.longPosition?.entry ?? "N/A"}</p>
                          <p>TP: {doc.longPosition?.tp ?? "N/A"}</p>
                          <p>SL: {doc.longPosition?.sl ?? "N/A"}</p>
                                                    <p>
                            <strong>Up Chance:</strong>{" "}
                            {doc.analysis?.upsideChance ?? "N/A"}
                          </p>
                          <p>
                            <strong>Down Chance:</strong>{" "}
                            {doc.analysis?.downsideChance ?? "N/A"}
                          </p>
                        </div>

                        <div
                          className="expanded-section"
                          style={{
                            border:
                              preferredDirectionByDoc[doc._id] === "short"
                                ? "2px solid #00ff88"
                                : "1px solid #444",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            setPreferredDirectionByDoc((prev) => ({
                              ...prev,
                              [doc._id]: "short",
                            }))
                          }
                        >
                          <h4>📉 Short Position</h4>
                          <p>Entry: {doc.shortPosition?.entry ?? "N/A"}</p>
                          <p>TP: {doc.shortPosition?.tp ?? "N/A"}</p>
                          <p>SL: {doc.shortPosition?.sl ?? "N/A"}</p>
                           <p>
                            <strong>Up Chance:</strong>{" "}
                            {doc.analysis?.upsideChance ?? "N/A"}
                          </p>
                          <p>
                            <strong>Down Chance:</strong>{" "}
                            {doc.analysis?.downsideChance ?? "N/A"}
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
